import * as React from 'react';
import { useTranslation } from 'next-i18next';
import Link from 'next/link';
import { Controller, useFormContext } from 'react-hook-form';
import { useAtomValue } from 'jotai';

import { config } from '@restworld/utils-common';
import { Checkbox } from '@restworld/ui-ds-base';

import { RestaurantOnboardingFormSection, RestaurantOnboardingPayload } from './restaurant-onboarding-form';
import EmailFieldWithSuggestions from './email-field-with-suggestions';
import {
  isOrgOnboardingModeAtom,
  orgOnboardingSaveAndContinueLaterAtom,
} from '../../organization-onboarding/use-organization-onboarding';

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface IRestaurantOrganizationOnboardingContactFormProps {}

type AgreementFieldName = keyof Pick<
  RestaurantOnboardingPayload,
  'privacy_policy' | 'terms_and_conditions' | 'vexatious_clauses_acceptance'
>;

const AgreementCheckbox: React.FC<{
  fieldName: AgreementFieldName;
  textKey: string;
  linkTextKey: string;
  link: string;
}> = ({ fieldName, textKey, linkTextKey, link }) => {
  const {
    control,
    formState: { errors },
  } = useFormContext<RestaurantOnboardingPayload>();
  const { t } = useTranslation('restaurant-onboarding');
  return (
    <div id={fieldName} className="flex flex-col gap-4">
      <Controller
        control={control}
        name={fieldName}
        rules={{ required: t('this_is_a_required_field') }}
        render={({ field }) => (
          <Checkbox
            id={fieldName}
            title={
              <label
                htmlFor={fieldName}
                className={
                  errors[fieldName]
                    ? 'text-red-600 text-base font-medium'
                    : 'text-neutral-700 text-base font-medium cursor-pointer'
                }
              >
                <span
                  onClick={(e) => {
                    e.stopPropagation();
                    field.onChange(!field.value);
                  }}
                >
                  {t(textKey)}{' '}
                </span>
                <Link href={link} target="_blank" rel="noopener noreferrer">
                  <span className="underline">{t(linkTextKey)}</span>
                </Link>
              </label>
            }
            isChecked={field.value}
            iserror={!!errors[fieldName]}
            {...field}
          />
        )}
      />
    </div>
  );
};

const RestaurantOrganizationOnboardingContactForm: React.FunctionComponent<
  IRestaurantOrganizationOnboardingContactFormProps
> = () => {
  const { t } = useTranslation('restaurant-onboarding');
  const isOrgOnboarding = useAtomValue(isOrgOnboardingModeAtom);
  const isSaveAndContinueLater = useAtomValue(orgOnboardingSaveAndContinueLaterAtom);

  const headerText = React.useMemo(() => {
    if (!isOrgOnboarding) return t('almost_done');
    if (isSaveAndContinueLater) return t('enter_your_contact_information_to_save_the_ad');
    return t('enter_your_contact_information_to_post_the_ad');
  }, [isOrgOnboarding, isSaveAndContinueLater, t]);

  return (
    <div className="inline-flex flex-col justify-start items-start gap-12 h-full w-full">
      <div className="self-stretch flex flex-col justify-start items-start gap-4">
        <h5 className="justify-start text-neutral-700 text-xl font-semibold">{headerText}</h5>
        {isOrgOnboarding ? (
          <h5 className="self-stretch justify-start text-neutral-500 text-base font-normal">
            {t('enter_your_contact_information_to_post_the_ad_subtitle')}
          </h5>
        ) : (
          <h5 className="self-stretch justify-start text-neutral-200 text-sm font-medium">
            {t('input_your_contact_to_complete_the_registration')}
          </h5>
        )}
      </div>
      <div className="self-stretch inline-flex flex-col justify-start items-start gap-6">
        <RestaurantOnboardingFormSection
          label={t('name')}
          placeholder=""
          name="name"
          required={t('name_is_required')}
        />
        <RestaurantOnboardingFormSection
          label={t('surname')}
          placeholder=""
          name="surname"
          required={t('surName_is_required')}
        />
        <EmailFieldWithSuggestions label={t('email')} required={t('enter_valid_email')} />
        <RestaurantOnboardingFormSection
          label={t('phone_number')}
          placeholder=""
          name="phone_number"
          required={t('enter_valid_phone_number')}
        />
      </div>
      <div className="self-stretch inline-flex flex-col justify-start items-start gap-8">
        <AgreementCheckbox
          fieldName="terms_and_conditions"
          textKey="agreement_terms_text"
          linkTextKey="agreement_terms_link"
          link={config.employerTermsUrl}
        />
        <AgreementCheckbox
          fieldName="vexatious_clauses_acceptance"
          textKey="agreement_vexatious_text"
          linkTextKey="agreement_vexatious_link"
          link={config.employerTermsUrl}
        />
        <AgreementCheckbox
          fieldName="privacy_policy"
          textKey="agreement_privacy_text"
          linkTextKey="agreement_privacy_link"
          link={config.restworldPrivacyUrl}
        />
      </div>
    </div>
  );
};

export default RestaurantOrganizationOnboardingContactForm;
